import { type ReactNode, useRef, useState } from "react";
import { Button } from "../actions/button";
import styles from "./image-picker.module.css";

export interface ImagePickerProps {
  /** The current image; the fallback shows when there is none or it fails to load. */
  src?: string | null;
  /** Shown instead of an image: initials, or an icon. */
  fallback: ReactNode;
  /**
   * A profile picture is round and a logo's mark square: both are cropped to their centre square
   * before upload. A wide one (a full, horizontal logo) is sent whole (a WebP as a PNG) and shown
   * in a strip.
   */
  shape?: "round" | "square" | "wide";
  /** Names the image for the buttons, e.g. "photo" or "logo". */
  noun: string;
  /** Uploads the chosen file; the buttons wait for it to settle. */
  onUpload: (file: File) => Promise<unknown>;
  /** Removes the image. Without it there is no Remove button. */
  onRemove?: () => Promise<unknown>;
}

/** The accepted types, as the API's image purposes allow them. */
const ACCEPT = "image/jpeg,image/png,image/webp";

/** Uploads are cropped to this many pixels square. */
const SIZE = 512;

/**
 * The centre square of an image, at most SIZE pixels, as WebP (PNG where the browser can't write
 * WebP; both keep a logo's transparency). Avatars and logos are drawn in square boxes, and Arc's
 * user menu stretches anything else. A file the browser can't decode goes up as it is, for the
 * API to refuse with its own message.
 */
async function squared(file: File): Promise<File> {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return file;
  }
  const side = Math.min(bitmap.width, bitmap.height);
  const out = Math.min(side, SIZE);
  const canvas = document.createElement("canvas");
  canvas.width = out;
  canvas.height = out;
  canvas
    .getContext("2d")
    ?.drawImage(bitmap, (bitmap.width - side) / 2, (bitmap.height - side) / 2, side, side, 0, 0, out, out);
  bitmap.close();
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/webp", 0.9));
  if (!blob) return file;
  const type = blob.type || "image/png";
  return new File([blob], `${file.name.replace(/\.[^.]+$/, "")}.${type === "image/webp" ? "webp" : "png"}`, { type });
}

/**
 * A wide image as the API takes it whole: a WebP is redrawn as a PNG (transparency kept), since a
 * full logo goes into PDFs and emails, which don't show WebP. Anything else goes up as it is.
 */
async function whole(file: File): Promise<File> {
  if (file.type !== "image/webp") return file;
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return file;
  }
  const canvas = document.createElement("canvas");
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  canvas.getContext("2d")?.drawImage(bitmap, 0, 0);
  bitmap.close();
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/png"));
  return blob ? new File([blob], `${file.name.replace(/\.[^.]+$/, "")}.png`, { type: "image/png" }) : file;
}

/**
 * Shows an image with Upload and Remove; the upload is cropped square first, unless it's wide. Errors are the
 * caller's to show: `onUpload` and
 * `onRemove` reject and the picker only stops waiting.
 */
export function ImagePicker({ src, fallback, shape = "square", noun, onUpload, onRemove }: ImagePickerProps) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState<"upload" | "remove" | null>(null);
  const [broken, setBroken] = useState<string | null>(null);
  const shown = src && src !== broken ? src : null;

  const run = async (kind: "upload" | "remove", action: () => Promise<unknown>) => {
    setBusy(kind);
    try {
      await action();
    } catch {
      // the caller shows the error
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className={styles.picker} data-shape={shape}>
      <span className={styles.preview}>
        {shown ? <img src={shown} alt="" onError={() => setBroken(shown)} /> : fallback}
      </span>
      <div className={styles.actions}>
        <div className={styles.buttons}>
          <Button
            size="sm"
            variant="secondary"
            loading={busy === "upload"}
            disabled={busy !== null}
            onClick={() => input.current?.click()}
          >
            {src ? `Change ${noun}` : `Upload ${noun}`}
          </Button>
          {src && onRemove ? (
            <Button
              size="sm"
              variant="ghost"
              loading={busy === "remove"}
              disabled={busy !== null}
              onClick={() => run("remove", onRemove)}
            >
              Remove
            </Button>
          ) : null}
        </div>
        <p className={styles.hint}>JPEG, PNG or WebP, up to 2 MB.</p>
      </div>
      <input
        ref={input}
        className={styles.input}
        type="file"
        accept={ACCEPT}
        aria-label={`Choose a ${noun}`}
        onChange={(event) => {
          const file = event.currentTarget.files?.[0];
          event.currentTarget.value = "";
          if (file)
            void run("upload", async () => onUpload(shape === "wide" ? await whole(file) : await squared(file)));
        }}
      />
    </div>
  );
}
